// Route Code Edited 7/17/2026
/**
 * route-cart-page-checkout-quote.js
 *
 * Appends the Route package protection quote to the cart PAGE's cloned
 * checkout button, e.g. "Checkout with package protection +$5.75".
 *
 * Separate from route-checkout-subtotal-update-slidecart.js (the slide-cart
 * drawer version) because this button has no pre-existing price to find and
 * swap — the original just says "Check Out", and Route's clone replaces the
 * entire text with a merchant-configured custom CTA string, also with no
 * price. So instead of locating and replacing an existing price span, this
 * script appends "+$X.XX" after whatever text is already on the button, and
 * re-applies that if anything resets it (e.g. Route re-rendering the button
 * after a cart AJAX update).
 *
 * Currency formatting matches Route's own widget (shopify-widget/src/app/
 * widget/utils/formatters.ts formatCurrency): Intl.NumberFormat('en-US',
 * {style:'currency', currency}) with the store's actual ISO currency code.
 * Mimicking the theme's own money format was tried first and caused visible
 * inconsistency between this button and Route's own widget header for
 * currencies without an en-US symbol (e.g. theme shows "Lek 400.00", Route's
 * widget shows "ALL 400.00" for the same amount).
 *
 * The cart total/quote here updates via AJAX in some flows (quantity
 * steppers, discount codes) without a full page reload, so this still needs
 * MutationObservers rather than a run-once-on-load script.
 *
 * Dependencies:
 *   - Route installed (detected via #route-widget-observer-root).
 *   - window.Shopify.currency.active for the store's ISO currency code
 *     (standard Shopify global, independent of theme formatMoney). Falls
 *     back to reading a currency code out of Route's own price element text
 *     if that global isn't present.
 *
 * Installation:
 *   Add this script tag at the bottom of layout/theme.liquid (before </body>),
 *   alongside (not instead of) the slide-cart drawer script — they target
 *   different buttons via different selectors and don't conflict.
 *     <script src="{{ 'route-cart-page-checkout-quote.js' | asset_url }}" defer></script>
 */
(function () {
  const ROUTE_PRICE_SELECTOR      = '#pw-coverage-description';
  const ROUTE_ROOT_ID             = 'route-widget-observer-root';
  const SEPARATOR_CHAR            = '+';
  const SEPARATOR_CLASS           = 'route-price-separator'; // for CSS-controlled spacing around the "+"
  const PRICE_FOUND_EVENT         = 'route-price-element-found';

  // Scoped to the cart page's checkout button class specifically, so this
  // script never touches an unrelated Route clone elsewhere on the page.
  const CLONED_BUTTON_SELECTOR    = '[data-route-cloned-button="true"].checkout__button';
  // name="checkout" is stripped from the clone by Route, so matching on it
  // reliably excludes the clone without needing a :not() guard — kept
  // anyway for defense in depth in case that stripping behavior changes.
  const ORIGINAL_BUTTON_SELECTOR  = 'button[name="checkout"].checkout__button:not([data-route-cloned-button])';

  const instanceState = new WeakMap();
  // { originalBtn, baseText, guardObserver, lastKnownGoodPrice, lastSetAriaLabel }

  let outerObserver = null;
  let routePriceObserver = null;
  let pendingScan = false;
  let priceElementSeen = false;

  // ─── Numeric parsing (for reading Route's own price element) ──────────────

  function extractNumericToken(text) {
    const match = text.match(/\d[\d.,]*\d|\d/);
    return match ? match[0] : null;
  }

  function parseNumericToken(token) {
    if (!token) return null;
    const sepMatches = [...token.matchAll(/[.,]/g)];
    if (!sepMatches.length) {
      const value = parseInt(token, 10);
      return isNaN(value) ? null : value;
    }
    const lastSep = sepMatches[sepMatches.length - 1];
    const decimalSep = token[lastSep.index];
    const groupSep = decimalSep === '.' ? ',' : '.';
    const normalized = token.split(groupSep).join('').replace(decimalSep, '.');
    const value = parseFloat(normalized);
    return isNaN(value) ? null : value;
  }

  function getStoreCurrencyCode() {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return null;
  }

  // Finds where a symbol/word prefix directly before a numeric token starts,
  // handling both glued ("$6.75") and space-separated ("ALL 400.00")
  // formats — used only as a fallback for sniffing a currency code out of
  // Route's own rendered text when window.Shopify.currency isn't available.
  function findPricePrefixStart(str, tokenIndex) {
    let start = tokenIndex;
    while (start > 0 && !/\s/.test(str[start - 1])) start--;
    if (start === tokenIndex && start > 0 && /\s/.test(str[start - 1])) {
      let wordStart = start - 1;
      while (wordStart > 0 && !/\s/.test(str[wordStart - 1])) wordStart--;
      if (wordStart < start - 1) start = wordStart;
    }
    return start;
  }

  // Reads Route's own quote value and, where possible, the currency it's
  // displayed in — preferring the Shopify global over sniffing Route's
  // rendered text (which only contains a distinguishable ISO code when Intl
  // falls back to one; USD/EUR/GBP etc. render as symbols instead).
  function getRoutePriceInfo() {
    const el = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!el) return null;
    const text = el.textContent;
    const token = extractNumericToken(text);
    if (!token) return null;
    const value = parseNumericToken(token);
    if (value === null) return null;

    let currencyCode = getStoreCurrencyCode();
    if (!currencyCode) {
      const tokenIndex = text.indexOf(token);
      const start = findPricePrefixStart(text, tokenIndex);
      const prefix = text.slice(start, tokenIndex).trim();
      if (/^[A-Z]{2,4}$/.test(prefix)) currencyCode = prefix;
    }
    return { value, currencyCode };
  }

  // ─── Currency formatting ────────────────────────────────────────────────────
  // Mirrors Route's own formatCurrency (shopify-widget/src/app/widget/utils/
  // formatters.ts) exactly, so this button's price matches Route's own
  // widget header rather than the theme's money format, which can visibly
  // diverge (e.g. theme "Lek 400.00" vs. Route's own "ALL 400.00").

  function formatCurrency(value, currencyCode) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  // ─── Structure ──────────────────────────────────────────────────────────────
  // The button's content is real DOM structure (text, then a
  // <b class="route-price-separator">+</b>, then price text) rather than a
  // single plain-text string, so CSS can control spacing around the "+"
  // independently of the text content.

  function structureMatchesDesired(clonedBtn, baseText, formattedPrice) {
    const nodes = clonedBtn.childNodes;
    if (nodes.length !== 3) return false;
    const [n0, n1, n2] = nodes;
    if (n0.nodeType !== Node.TEXT_NODE || n0.textContent !== baseText) return false;
    if (n1.nodeType !== Node.ELEMENT_NODE || !n1.classList.contains(SEPARATOR_CLASS) || n1.textContent !== SEPARATOR_CHAR) return false;
    if (n2.nodeType !== Node.TEXT_NODE || n2.textContent !== formattedPrice) return false;
    return true;
  }

  function buildDesiredStructure(clonedBtn, baseText, formattedPrice) {
    while (clonedBtn.firstChild) clonedBtn.removeChild(clonedBtn.firstChild);
    clonedBtn.appendChild(document.createTextNode(baseText));
    const b = document.createElement('b');
    b.className = SEPARATOR_CLASS;
    b.textContent = SEPARATOR_CHAR;
    clonedBtn.appendChild(b);
    clonedBtn.appendChild(document.createTextNode(formattedPrice));
  }

  // ─── Pairing ─────────────────────────────────────────────────────────────────

  function findPairedOriginal(clonedBtn) {
    let node = clonedBtn.parentElement;
    while (node) {
      const matches = node.querySelectorAll(ORIGINAL_BUTTON_SELECTOR);
      if (matches.length >= 1) return matches[0];
      node = node.parentElement;
    }
    return null;
  }

  // ─── Guard ───────────────────────────────────────────────────────────────────

  function attachGuard(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;
    if (state.guardObserver) state.guardObserver.disconnect();

    state.guardObserver = new MutationObserver(() => {
      if (state.lastKnownGoodPrice === null) return;
      if (!structureMatchesDesired(clonedBtn, state.baseText, state.lastKnownGoodPrice)) {
        buildDesiredStructure(clonedBtn, state.baseText, state.lastKnownGoodPrice);
      }
    });

    state.guardObserver.observe(clonedBtn, { childList: true, characterData: true, subtree: true });
  }

  // ─── Reconcile ───────────────────────────────────────────────────────────────

  function reconcile(clonedBtn) {
    const state = instanceState.get(clonedBtn);
    if (!state) return;

    const info = getRoutePriceInfo();
    if (!info) return;

    const formatted = formatCurrency(info.value, info.currencyCode);

    if (structureMatchesDesired(clonedBtn, state.baseText, formatted)) return;

    if (state.guardObserver) state.guardObserver.disconnect();
    buildDesiredStructure(clonedBtn, state.baseText, formatted);
    state.lastKnownGoodPrice = formatted;
    attachGuard(clonedBtn);

    // aria-label has no DOM structure of its own, so it gets a
    // space-separated plain-text equivalent, kept in sync with the price on
    // every write (unlike the drawer script, this doesn't defensively
    // preserve an existing aria-label, since the visible text is changing).
    const desiredAriaLabel = `${state.baseText} ${SEPARATOR_CHAR}${formatted}`;
    clonedBtn.setAttribute('aria-label', desiredAriaLabel);
    state.lastSetAriaLabel = desiredAriaLabel;
  }

  function reconcileAll() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (instanceState.has(btn)) reconcile(btn);
    });
  }

  // ─── Shared Route price watcher ─────────────────────────────────────────────

  function watchRoutePrice() {
    const routePriceEl = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!routePriceEl) return;
    if (routePriceObserver) routePriceObserver.disconnect();

    routePriceObserver = new MutationObserver(() => reconcileAll());
    routePriceObserver.observe(routePriceEl, { characterData: true, childList: true, subtree: true });
  }

  function checkMutationsForPriceElement(mutationList) {
    if (priceElementSeen) return;
    for (const mutation of mutationList) {
      for (const addedNode of mutation.addedNodes) {
        if (addedNode.nodeType !== Node.ELEMENT_NODE) continue;
        if (addedNode.matches && addedNode.matches(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
        if (addedNode.querySelector && addedNode.querySelector(ROUTE_PRICE_SELECTOR)) {
          priceElementSeen = true;
          document.dispatchEvent(new CustomEvent(PRICE_FOUND_EVENT));
          return;
        }
      }
    }
  }

  document.addEventListener(PRICE_FOUND_EVENT, () => {
    watchRoutePrice();
    reconcileAll();
  });

  // ─── Cloned button setup ────────────────────────────────────────────────────

  function processClonedButton(btn) {
    if (instanceState.has(btn)) return;

    const originalBtn = findPairedOriginal(btn);
    // Captured once, before this script ever appends anything — the base we
    // always append "+$X.XX" onto, whether that's a merchant custom CTA
    // string or whatever Route set.
    const baseText = (btn.textContent || '').trim();

    instanceState.set(btn, {
      originalBtn,
      baseText,
      guardObserver: null,
      lastKnownGoodPrice: null,
      lastSetAriaLabel: null,
    });

    attachGuard(btn);
    reconcile(btn);
  }

  function scanForClonedButtons() {
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      if (!instanceState.has(btn)) {
        processClonedButton(btn);
      } else {
        reconcile(btn);
      }
    });
    watchRoutePrice();
  }

  // ─── Outer observer ──────────────────────────────────────────────────────────
  // Only rescan when a mutation's addedNodes actually contains — or contains
  // a descendant matching — a new cloned button. A cart page re-rendering
  // its totals/line items via AJAX can produce unrelated childList churn
  // that shouldn't trigger a full rescan.

  function mutationsContainNewClonedButton(mutationList) {
    for (const mutation of mutationList) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches && node.matches(CLONED_BUTTON_SELECTOR)) return true;
        if (node.querySelector && node.querySelector(CLONED_BUTTON_SELECTOR)) return true;
      }
    }
    return false;
  }

  function attachObserver() {
    if (outerObserver) outerObserver.disconnect();
    if (document.querySelector(ROUTE_PRICE_SELECTOR)) priceElementSeen = true;

    outerObserver = new MutationObserver((mutationList) => {
      checkMutationsForPriceElement(mutationList);

      if (!mutationsContainNewClonedButton(mutationList)) return;

      if (pendingScan) return;
      pendingScan = true;
      requestAnimationFrame(() => {
        pendingScan = false;
        scanForClonedButtons();
      });
    });

    outerObserver.observe(document.body, { childList: true, subtree: true });
    scanForClonedButtons();
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  function waitForRoot() {
    if (document.getElementById(ROUTE_ROOT_ID)) {
      attachObserver();
      return;
    }

    let initTimeout = null;
    const bodyObserver = new MutationObserver(() => {
      if (!document.getElementById(ROUTE_ROOT_ID)) return;
      bodyObserver.disconnect();
      clearTimeout(initTimeout);
      attachObserver();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });
    initTimeout = setTimeout(() => bodyObserver.disconnect(), 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRoot);
  } else {
    waitForRoot();
  }

  // ─── bfcache ────────────────────────────────────────────────────────────────

  window.addEventListener('pagehide', () => {
    if (outerObserver) outerObserver.disconnect();
    if (routePriceObserver) routePriceObserver.disconnect();
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      const state = instanceState.get(btn);
      if (state && state.guardObserver) state.guardObserver.disconnect();
    });
  });

  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    priceElementSeen = false;
    document.querySelectorAll(CLONED_BUTTON_SELECTOR).forEach((btn) => {
      instanceState.delete(btn);
    });
    attachObserver();
  });
})();